// Pure rules for "range" sources in Custom Views and the Ops dashboard: a curated dataset (S3_DATA with
// metadata.curated), read over a time window through the dataset query rather than from one file. A HISTORICAL
// model's data is such a dataset (the one its runs write), so there is no separate model-output range source.
// The fetch itself lives in seriesSourcesFetch.ts so this module stays free of the Amplify client and testable
// with Jest. See docs/architecture/HISTORICAL_MODEL_CONSUMERS.md.

import type { DatasetQueryAggregation, DatasetQueryResult } from "./datasetQueryApi";
import { isFlagColumn } from "./datasetQueryApi";

/** What a range source reads: one column of one curated dataset (decision 3: one line per source). */
export type SeriesRef = { kind: "dataset"; id: string; column: string };

/** A fetch window; both ends inclusive as the chart sees them (the fetch makes the end exclusive). */
export interface SeriesWindow {
  start: number;
  end: number;
}

/** One fetched line, in the shape the Custom View overlays and the Ops candidates already take. */
export interface SeriesLine {
  timestamps: Date[];
  values: number[];
  unit?: string;
  label?: string;
  /** observation | derived | forecast when the processor declared it; null when it didn't. */
  kind: string | null;
  aggregation: DatasetQueryAggregation | null;
}

export const DEFAULT_RANGE_LOOKBACK_DAYS = 30;
export const DAY_MS = 24 * 60 * 60 * 1000;

/** A forecast output (one file per issue): null / FILES. Not DATASET (a HISTORICAL model's output, whose rows are in a dataset). */
export const isForecastFilesOutput = (output: { storageMode?: string | null } | null | undefined): boolean =>
  !output?.storageMode || output.storageMode === "FILES";

export { isFlagColumn };

/** A dataset's columns (JSON string or array) as plain names, without the time column. */
export const datasetColumnNames = (raw: unknown): string[] => {
  let parsed: unknown = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = [];
    }
  }
  if (!Array.isArray(parsed)) return [];
  return parsed
    .map((c: any) => (typeof c === "string" ? c : c?.name))
    .filter((name): name is string => typeof name === "string" && name.trim() !== "")
    .filter((name) => !/^(timestamp|time|datetime|date|datetime \(utc\))$/i.test(name))
    .filter((name) => !isFlagColumn(name));
};

const parseMeta = (raw: unknown): Record<string, any> => {
  if (!raw) return {};
  if (typeof raw === "object") return raw as Record<string, any>;
  if (typeof raw !== "string") return {};
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
};

/** A saved source as a range reference, or null when it is a forecast, a Postgres feed or a pinned-file dataset. */
export const rangeSourceRef = (ds: any): SeriesRef | null => {
  if (!ds) return null;
  const meta = parseMeta(ds.metadata);
  if (ds.sourceType === "S3_DATA" && meta.datasetId && meta.curated && meta.column) {
    return { kind: "dataset", id: meta.datasetId, column: meta.column };
  }
  return null;
};

/** The lookback a saved range source carries (decision 2: like a Postgres feed), else the default. */
export const rangeSourceLookbackDays = (ds: any, fallback = DEFAULT_RANGE_LOOKBACK_DAYS): number => {
  const days = Number(parseMeta(ds?.metadata).lookbackDays);
  return Number.isFinite(days) && days > 0 ? days : fallback;
};

/**
 * The window range lines are fetched for (decision 2 A).
 * - With a base forecast: the chart's own clip window — the issue's span, plus as much again before it (at least a
 *   day), ending where the issue ends — so the lines fill exactly what the chart shows.
 * - Without one: `lookbackDays` ending at the newest data end given; null when no end is known, which tells the
 *   fetch to take the server's default window (the last 30 days ending at the newest data).
 */
export const windowFor = (
  base: SeriesWindow | null | undefined,
  lookbackDays: number = DEFAULT_RANGE_LOOKBACK_DAYS,
  newestEnd?: number | null
): SeriesWindow | null => {
  if (base && Number.isFinite(base.start) && Number.isFinite(base.end)) {
    const span = Math.max(base.end - base.start, DAY_MS);
    return { start: base.start - span, end: base.end };
  }
  if (newestEnd !== undefined && newestEnd !== null && Number.isFinite(newestEnd)) {
    return { start: newestEnd - lookbackDays * DAY_MS, end: newestEnd };
  }
  return null;
};

/** The query answer's one column as points; a null or non-numeric cell is dropped, the rest keep their stamps. */
export const pointsFrom = (
  answer: Pick<DatasetQueryResult, "timestamps" | "series"> | null | undefined,
  column: string
): { timestamps: Date[]; values: number[] } => {
  const timestamps: Date[] = [];
  const values: number[] = [];
  const cells = answer?.series?.[column] ?? answer?.series?.[column.toLowerCase()] ?? [];
  (answer?.timestamps ?? []).forEach((stamp, index) => {
    const value = cells[index];
    // A blank text cell is a gap, not 0 (Number("") is 0)
    const number = typeof value === "number" ? value : typeof value === "string" && value.trim() !== "" ? Number(value) : NaN;
    if (!Number.isFinite(number)) return;
    const at = new Date(stamp);
    if (Number.isNaN(at.getTime())) return;
    timestamps.push(at);
    values.push(number);
  });
  return { timestamps, values };
};

/** The query's start/end strings for a window: start inclusive, end exclusive, so the last point is kept. */
export const windowQueryParams = (window: SeriesWindow): { start: string; end: string } => ({
  start: new Date(window.start).toISOString(),
  end: new Date(window.end + 1000).toISOString(),
});
